import { useState, useCallback } from "react";
import {
  ChevronRight,
  ChevronDown,
  Folder,
  FolderOpen,
  FileText,
  FileCode2,
  FileJson,
  FileImage,
  File,
} from "lucide-react";
import type { FileTreeNode } from "../types";
import { CopyWrapper } from "./copy-wrapper";

const LANGUAGE_ICONS: Record<string, React.ElementType> = {
  typescript: FileCode2,
  javascript: FileCode2,
  tsx: FileCode2,
  jsx: FileCode2,
  python: FileCode2,
  go: FileCode2,
  rust: FileCode2,
  java: FileCode2,
  json: FileJson,
  yaml: FileJson,
  yml: FileJson,
  toml: FileJson,
  png: FileImage,
  jpg: FileImage,
  jpeg: FileImage,
  gif: FileImage,
  svg: FileImage,
  webp: FileImage,
  md: FileText,
  txt: FileText,
  csv: FileText,
};

function getFileIcon(node: FileTreeNode): React.ElementType {
  if (node.type === "directory") return Folder;
  if (node.language && LANGUAGE_ICONS[node.language]) return LANGUAGE_ICONS[node.language];
  const ext = node.name.split(".").pop()?.toLowerCase();
  if (ext && LANGUAGE_ICONS[ext]) return LANGUAGE_ICONS[ext];
  return File;
}

function treeToString(nodes: FileTreeNode[], prefix = ""): string {
  return nodes
    .map((node, i) => {
      const isLast = i === nodes.length - 1;
      const connector = isLast ? "└── " : "├── ";
      const line = prefix + connector + node.name;
      if (node.type === "directory" && node.children?.length) {
        const childPrefix = prefix + (isLast ? "    " : "│   ");
        return line + "\n" + treeToString(node.children, childPrefix);
      }
      return line;
    })
    .join("\n");
}

interface FileTreeItemProps {
  node: FileTreeNode;
  depth: number;
}

function FileTreeItem({ node, depth }: FileTreeItemProps) {
  const [expanded, setExpanded] = useState(depth < 2);
  const isDir = node.type === "directory";
  const hasChildren = isDir && node.children && node.children.length > 0;
  const Icon = expanded && isDir ? FolderOpen : getFileIcon(node);

  const toggle = useCallback(() => {
    if (isDir) setExpanded((p) => !p);
  }, [isDir]);

  return (
    <div>
      <button
        type="button"
        className={`flex w-full items-center gap-1 rounded px-1 py-0.5 text-left text-sm transition-colors hover:bg-accent ${
          isDir ? "cursor-pointer" : "cursor-default"
        }`}
        style={{ paddingLeft: `${depth * 16 + 4}px` }}
        onClick={toggle}
        tabIndex={isDir ? 0 : -1}
      >
        {isDir ? (
          expanded ? (
            <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          )
        ) : (
          <span className="inline-block w-3.5" />
        )}
        <Icon
          className={`h-4 w-4 shrink-0 ${
            isDir
              ? "text-amber-500 dark:text-amber-400"
              : "text-muted-foreground"
          }`}
        />
        <span className={isDir ? "font-medium" : ""}>{node.name}</span>
      </button>
      {expanded && hasChildren && (
        <div>
          {node.children!.map((child, i) => (
            <FileTreeItem key={`${child.name}-${i}`} node={child} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  );
}

interface FileTreeBlockProps {
  tree: FileTreeNode[];
}

export function FileTreeBlock({ tree }: FileTreeBlockProps) {
  const textRepresentation = treeToString(tree);

  return (
    <CopyWrapper copyText={textRepresentation} label="Copy file tree" className="my-2">
      <div className="rounded-lg border border-border bg-muted/20 p-2">
        <div className="mb-1 flex items-center gap-1.5 px-1 text-xs font-medium text-muted-foreground">
          <Folder className="h-3.5 w-3.5" />
          File Tree
        </div>
        <div className="max-h-[400px] overflow-auto">
          {tree.map((node, i) => (
            <FileTreeItem key={`${node.name}-${i}`} node={node} depth={0} />
          ))}
        </div>
      </div>
    </CopyWrapper>
  );
}
